import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Coffee, ArrowRight, Mail, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function WaiterLogin() {
  const { api, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 4) {
      toast.error('Please enter your 4-digit code');
      return;
    }

    setLoading(true);
    setEmailUnverified(false);
    
    try {
      const response = await api.post('/waiter/login', { code: code.toUpperCase() });
      
      // Store token and user using loginWithToken
      loginWithToken(response.data.user, response.data.access_token);
      
      toast.success(`Welcome, ${response.data.user.name}!`);
      
      // Use window.location for hard redirect to ensure state is fresh
      window.location.href = '/waiter';
    } catch (error) {
      const detail = error.response?.data?.detail || 'Invalid code';
      
      // Check for email verification error
      if (detail.includes('not verified') || error.response?.headers?.['x-email-unverified']) {
        setEmailUnverified(true);
        setUnverifiedEmail(error.response?.headers?.['x-user-email'] || '');
        toast.error('Email verification required');
      } else {
        toast.error(detail);
      }
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) {
      toast.error('Email address not found. Please contact your manager.');
      return;
    }
    
    setResendingVerification(true);
    try {
      await api.post('/auth/resend-verification', { email: unverifiedEmail });
      toast.success('Verification code sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (value.length <= 4) {
      setCode(value);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Coffee className="w-8 h-8 text-teal-600" />
          </div>
          <CardTitle className="text-2xl">Waiter Login</CardTitle>
          <CardDescription>Enter your 4-digit code to start your shift</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Email Verification Warning */}
          {emailUnverified && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-amber-800">Email Verification Required</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Your email address hasn't been verified yet. Please check your inbox for a verification code.
                  </p>
                  {unverifiedEmail && (
                    <p className="text-xs text-amber-600 mt-2">
                      Sent to: {unverifiedEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3')}
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                    className="mt-3 border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    {resendingVerification ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Resend Verification Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="Enter 4-digit code"
                value={code}
                onChange={handleCodeChange}
                className="text-center text-3xl font-mono tracking-widest h-16"
                maxLength={4}
                autoFocus
                data-testid="waiter-code-input"
              />
              <p className="text-xs text-center text-slate-500">
                Get your code from the restaurant manager
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading || code.length !== 4}
              data-testid="waiter-login-btn"
            >
              {loading ? 'Logging in...' : 'Start Shift'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-slate-500">
              Need help? Contact your manager
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
